import { SessionStorage } from "@/app/storage"
import { Button } from "@/components/buttons/button"
import CustomHead from "@/components/layouts/custom_head"
import { CaretDown, CaretRight } from "@phosphor-icons/react"
import { useRollbar } from "@rollbar/react"
import Image from "next/image"
import { useRouter } from "next/router"
import { useEffect, useState } from "react"

interface ErrorPageProps {
  error?: Error | null
}

export default function ErrorPage({ error }: ErrorPageProps) {
  const rollbar = useRollbar()
  const { query } = useRouter()
  // query parameters that are optional
  const { message, statusCode } = query

  const [openErrorDetailBox, setOpenErrorDetailBox] = useState(false)

  // if error is passed through props, then use it
  // otherwise use query parameters to get error info
  const errorMessage =
    error ?
      error.message
    : `Error Code ${statusCode ?? "unknown"}: ${message ?? ""}`

  useEffect(() => {
    // Global Data uses HOC to make sure its always loaded (method globalData.isLoaded() returns boolean)
    // if any part of Global Data fails to be loaded, it will probably get stuck in Loading screen
    // so we fetch previous and replace current Global Data in Session Storage, assuming the current one failed to load/refresh
    const previousData = SessionStorage.get("prevGlobalData")
    // removes current Global Data due to possible error during refreshing
    SessionStorage.remove("globalData")
    // restores Global Data data (if available) to ensure App won't get stuck again
    if (previousData) SessionStorage.set("globalData", previousData)

    // report error to Rollbar
    rollbar.error(error || new Error(errorMessage))
  }, [])

  return (
    <>
      <CustomHead title="Error" />
      <main className="relative flex h-[calc(100vh-8rem)] flex-col">
        <header className="flex h-14 w-full justify-start bg-main-200 px-2 xl:hidden">
          <Image
            src="/images/grafiniti-logo.png"
            width={200}
            height={200}
            alt="Grafiniti Logo"
            className="h-12 w-32"
          />
        </header>
        <section className="flex flex-1 flex-col items-center justify-center">
          <div className="flex flex-col gap-4 text-center">
            <h1 className="text-2xl font-bold">Uh oh, something went wrong</h1>
            <p className="text-left leading-relaxed sm:text-sm">
              Click on the arrow below to see more information.
            </p>
            <div className="flex h-44 max-w-96 flex-col items-start justify-start gap-1 px-2">
              <div
                onClick={() => setOpenErrorDetailBox(!openErrorDetailBox)}
                className="flex items-center gap-1">
                <span className="inline-block text-sm font-semibold">
                  Error details
                </span>
                {!openErrorDetailBox ?
                  <CaretRight />
                : <CaretDown />}
              </div>
              {openErrorDetailBox ?
                <div className="h-full w-full overflow-scroll rounded-md border border-main-300 bg-main-50 p-1.5 text-left text-xs">
                  <pre className="whitespace-pre-wrap font-OS">
                    {errorMessage}
                  </pre>
                </div>
              : null}
            </div>
            <div className="flex justify-center gap-4">
              <Button
                onClick={() => window.location.replace("/user/support")}
                color="primary">
                Contact Support
              </Button>
              <Button
                onClick={() => window.location.replace("/home")}
                color="tertiary"
                className="text-main-light">
                Home
              </Button>
            </div>
          </div>
        </section>
      </main>
    </>
  )
}
